import { setLocalCart } from '@/redux/cart/cart.local';
import { getCartApi } from '@/services/cart.service';
import { useQueryClient } from '@tanstack/react-query';

/**
 * A simple hook that provides a function to refresh the cart from the server
 * This can be called after cart mutations to ensure the UI displays the correct cart
 */
export function useRefreshCart() {
  const queryClient = useQueryClient();

  /**
   * Force refresh the cart from the server
   * This helps overcome inconsistencies in API response formats
   */
  const refreshCart = async () => {
    try {
      // Fetch the latest cart data directly from the server
      const response = await getCartApi();
      console.log('Refreshing cart data, server response:', response);

      // Extract cart items based on response format
      let cartItems = [];

      if (response) {
        if (response.items && Array.isArray(response.items)) {
          // Server returned object with items array
          cartItems = response.items;
        } else if (Array.isArray(response)) {
          // Server returned array directly
          cartItems = response;
        }
      }

      console.log('Refreshed cart items:', cartItems);

      // Update local storage and React Query cache
      setLocalCart(cartItems);

      // 🎯 FIXED: Return consistent format for useCart hook
      const cartData = {
        items: cartItems,
        validation: response?.validation || null,
        cleanup: response?.cleanup || null,
        totalItems: cartItems.length,
        totalQuantity: cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
        isEmpty: cartItems.length === 0,
        hasValidationIssues: response?.validation && !response.validation.is_valid,
        hasCleanupItems: response?.cleanup && response.cleanup.removed_count > 0,
      };

      queryClient.setQueryData(['cart'], cartData);

      return cartData;
    } catch (error) {
      console.error('Error refreshing cart:', error);
      throw error;
    }
  };

  /**
   * 🎯 NEW: Remove only specific purchased items from cart
   * This preserves unpurchased items in the cart
   * @param {Array} purchasedItemIds - Array of item IDs that were purchased
   */
  const removeItemsAfterPayment = async (purchasedItemIds = []) => {
    try {
      console.log('🎯 Removing purchased items from cart:', purchasedItemIds);

      if (!Array.isArray(purchasedItemIds) || purchasedItemIds.length === 0) {
        console.log('🎯 No items to remove, keeping cart as is');
        return await refreshCart();
      }

      // Get current cart from React Query cache or refresh from server
      let currentCart = queryClient.getQueryData(['cart']);

      if (!currentCart) {
        console.log('🎯 No cached cart found, refreshing from server...');
        currentCart = await refreshCart();
      }

      // Extract items array from cart data (handle both formats)
      let cartItemsArray = [];
      if (currentCart.items && Array.isArray(currentCart.items)) {
        cartItemsArray = currentCart.items;
      } else if (Array.isArray(currentCart)) {
        cartItemsArray = currentCart;
      }

      // Filter out purchased items, keep the rest
      const remainingItems = cartItemsArray.filter((item) => !purchasedItemIds.includes(item.id));

      console.log('🎯 Items remaining after purchase:', {
        originalCount: cartItemsArray.length,
        purchasedCount: purchasedItemIds.length,
        remainingCount: remainingItems.length,
        remainingItems: remainingItems.map((item) => ({ id: item.id, name: item.name })),
      });

      // Update local storage and React Query cache with remaining items
      setLocalCart(remainingItems);

      // Update cache with correct format for useCart hook
      const updatedCartData = {
        items: remainingItems,
        validation: currentCart.validation || null,
        cleanup: currentCart.cleanup || null,
        totalItems: remainingItems.length,
        totalQuantity: remainingItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
        isEmpty: remainingItems.length === 0,
        hasValidationIssues: false,
        hasCleanupItems: false,
      };

      queryClient.setQueryData(['cart'], updatedCartData);

      return updatedCartData;
    } catch (error) {
      console.error('❌ Error removing purchased items from cart:', error);
      // Fallback: refresh cart from server
      return await refreshCart();
    }
  };

  /**
   * 🎯 UPDATED: Force clear and refresh cart after payment (ONLY use for full cart purchases)
   * For partial purchases, use removeItemsAfterPayment instead
   */
  const clearCartAfterPayment = async () => {
    try {
      console.log('🎯 Force clearing ENTIRE cart after payment...');
      console.warn('⚠️ This should only be used when ALL cart items were purchased!');

      // 1. Clear local storage immediately
      setLocalCart([]);

      // 2. Clear React Query cache
      const emptyCartData = {
        items: [],
        validation: null,
        cleanup: null,
        totalItems: 0,
        totalQuantity: 0,
        isEmpty: true,
        hasValidationIssues: false,
        hasCleanupItems: false,
      };
      queryClient.setQueryData(['cart'], emptyCartData);
      queryClient.removeQueries(['cart']);

      // 3. Wait a bit for backend to process
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // 4. Fetch fresh data from server
      const freshCart = await refreshCart();

      console.log('🎯 Cart cleared and refreshed:', freshCart);
      return freshCart;
    } catch (error) {
      console.error('❌ Error clearing cart after payment:', error);
      // Even if API fails, keep local clear
      setLocalCart([]);
      const emptyCartData = {
        items: [],
        validation: null,
        cleanup: null,
        totalItems: 0,
        totalQuantity: 0,
        isEmpty: true,
        hasValidationIssues: false,
        hasCleanupItems: false,
      };
      queryClient.setQueryData(['cart'], emptyCartData);
      return emptyCartData;
    }
  };

  return {
    refreshCart,
    clearCartAfterPayment,
    removeItemsAfterPayment,
  };
}
